import React, { memo, useMemo, useCallback, useState, useContext, useRef, useEffect } from 'react';

import { useWeb5Context } from 'lib/Web5Context';

import { publish } from 'lib/EventBuser';

import { REFRESH_IMPORT_AFTER_CREATE_FIELD } from 'lib/events';
import { LOGEVENT_SUBMODULE } from 'shards/Fields/constants';

import FormExtraField from 'shards/Fields/FormExtraField';
import { getMappedFieldList } from 'shards/Fields/lib';
import { getEntityFromString } from 'utils/getEntityFromString';

import ImportContext from '../../ImportContext';

const CreateFieldModal = memo(
    ({ formExtraFieldRef, setFormExtraFieldRef, idSetupRole, entityName }) => {
        const [fieldsData, setFieldsData] = useState(null);
        const isFirstRender = useRef(true);

        const { t, language: lang, setDeviceCache, customGeneralTab, logEvent } = useWeb5Context();
        const { language: externalLanguage, ...overrides } = useContext(ImportContext);
        const language = externalLanguage || lang;

        const entityLogProps = useMemo(() => {
            const entity = getEntityFromString(entityName);
            const entityField = entity?.fields || entityName;
            const submodule = LOGEVENT_SUBMODULE[entityField];
            return { entityLogName: 'fields', entityLogSubmodule: submodule };
        }, [entityName]);

        const getEntityData = useCallback(
            (entity) => {
                return new Promise((resolve, reject) => {
                    getMappedFieldList(entity, language, t, customGeneralTab)
                        .then((data) => {
                            if (!data) {
                                reject();
                            } else {
                                setFieldsData({ ...fieldsData, [entity]: data });
                                resolve(data);
                            }
                        })
                        .catch(reject);
                });
            },
            [language, customGeneralTab, t, fieldsData],
        );

        useEffect(() => {
            if (isFirstRender.current) {
                isFirstRender.current = false;
                const entity = getEntityFromString(entityName);
                getEntityData(entity?.fields || entityName);
            }
        }, [getEntityData, entityName]);

        const processDataBeforeSave = useCallback((data) => {
            if (!data || Object.keys(data)?.length === 0) return data;
            return Object.entries(data).reduce((obj, [key, value]) => {
                if (key === 'customValidation' && !value) {
                    obj[key] = -1;
                } else {
                    obj[key] = value;
                }
                return obj;
            }, {});
        }, []);

        const refreshList = useCallback(
            ({ response, rowIndex }) => {
                const createdField = response?.data ? response.data : null;
                if (!createdField) return;
                logEvent({ event: 'settings', submodule: 'import', functionality: 'createField' });

                publish(REFRESH_IMPORT_AFTER_CREATE_FIELD, { field: createdField, rowIndex });
            },
            [logEvent],
        );

        if (!fieldsData) return;

        return (
            <FormExtraField
                onRef={(ref) => {
                    !formExtraFieldRef && setFormExtraFieldRef(ref);
                }}
                setDeviceCache={setDeviceCache}
                fieldsData={fieldsData}
                getEntityData={getEntityData}
                idSetupRole={idSetupRole}
                processDataBeforeSave={processDataBeforeSave}
                {...entityLogProps}
                avoidTours={true}
                overrides={overrides}
                refreshList={refreshList}
            />
        );
    },
);

export default CreateFieldModal;
